import { useAuth } from '../context/AuthContext';
import {
  Permission,
  Role,
  hasPermission,
  hasAnyPermission,
  hasAllPermissions,
  isAdminOrOwner,
  isOwner,
  isManager,
  isAdmin,
  isMember,
  isViewer,
  hasRole,
  hasAnyRole,
  normalizeRole,
} from '../services/permissions/permissions.service';

/**
 * Hook para verificar permissões do usuário
 *
 * Role and permissions are derived from the active workspace membership
 * (set in AuthContext via WorkspaceService.resolveMembership). This is the
 * single source of truth — localStorage is only used as a fallback during
 * session recovery before the membership API response arrives.
 */
export function usePermissions() {
  const {
    currentWorkspaceRole,
    currentWorkspacePermissions,
    user,
  } = useAuth();

  // Fallback: if no role is set yet (e.g. API not loaded), derive from user/dbUser
  const role: Role = currentWorkspaceRole || normalizeRole(
    (user as any)?.role || (user as any)?.dbUser?.role,
  );

  const permissions: Permission[] =
    currentWorkspacePermissions.length > 0
      ? currentWorkspacePermissions
      : [];

  return {
    can: (permission: Permission) => hasPermission(permissions, role, permission),
    canAny: (perms: Permission[]) => hasAnyPermission(permissions, role, perms),
    canAll: (perms: Permission[]) => hasAllPermissions(permissions, role, perms),
    isAdminOrOwner: () => isAdminOrOwner(role),
    isOwner: () => isOwner(role),
    isAdmin: () => isAdmin(role),
    isManager: () => isManager(role),
    isMember: () => isMember(role),
    isViewer: () => isViewer(role),
    hasRole: (target: Role) => hasRole(target, role),
    hasAnyRole: (targets: Role[]) => hasAnyRole(targets, role),
    role,
    permissions,
    isInitialized: currentWorkspaceRole !== null,
  };
}
